import { create } from "zustand";
import type { TChapterStatusMap, TSetupProgress } from "@/zapi-frontend/shared/hooks/useProjectSetupPolling";

type TGenerationStatus =
  | "pending"
  | "processing"
  | "streaming"
  | "ready"
  | "processed"
  | "error"
  | "errorSeen"
  | "cancelled";

interface ProjectSetupStore {
  activeSetupTasks: Record<string, number>;
  taskStatuses: Record<string, TGenerationStatus>;
  pollingProjects: Record<string, boolean>;
  chapterStatusMaps: Record<string, TChapterStatusMap | null>;
  setupProgress: Record<string, TSetupProgress | null>;
  setupFailures: Record<string, string>;
  setSetupTask: (projectId: string, taskId: number) => void;
  updateTaskStatus: (projectId: string, status: TGenerationStatus) => void;
  setChapterStatusMap: (projectId: string, map: TChapterStatusMap | null) => void;
  setSetupProgress: (projectId: string, setup: TSetupProgress | null) => void;
  setSetupFailure: (projectId: string, error: string) => void;
  clearSetupFailure: (projectId: string) => void;
  clearSetupTask: (projectId: string) => void;
  getSetupTask: (projectId: string) => number | null;
  setPolling: (projectId: string, isPolling: boolean) => void;
}

export const useProjectSetupStore = create<ProjectSetupStore>()((set, get) => ({
  activeSetupTasks: {},
  taskStatuses: {},
  pollingProjects: {},
  chapterStatusMaps: {},
  setupProgress: {},
  setupFailures: {},

  setSetupTask: (projectId, taskId) =>
    set((state) => ({
      activeSetupTasks: { ...state.activeSetupTasks, [projectId]: taskId }
    })),

  updateTaskStatus: (projectId, status) =>
    set((state) => ({
      taskStatuses: { ...state.taskStatuses, [projectId]: status }
    })),

  setChapterStatusMap: (projectId, map) =>
    set((state) => ({
      chapterStatusMaps: { ...state.chapterStatusMaps, [projectId]: map }
    })),

  setSetupProgress: (projectId, setup) =>
    set((state) => ({
      setupProgress: { ...state.setupProgress, [projectId]: setup }
    })),

  setSetupFailure: (projectId, error) =>
    set((state) => ({
      setupFailures: { ...state.setupFailures, [projectId]: error }
    })),

  clearSetupFailure: (projectId) =>
    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [projectId]: _failure, ...restFailures } = state.setupFailures;
      return { setupFailures: restFailures };
    }),

  clearSetupTask: (projectId) =>
    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [projectId]: _task, ...restTasks } = state.activeSetupTasks;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [projectId]: _status, ...restStatuses } = state.taskStatuses;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [projectId]: _map, ...restMaps } = state.chapterStatusMaps;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [projectId]: _setup, ...restSetup } = state.setupProgress;
      return {
        activeSetupTasks: restTasks,
        taskStatuses: restStatuses,
        chapterStatusMaps: restMaps,
        setupProgress: restSetup
      };
    }),

  getSetupTask: (projectId) => get().activeSetupTasks[projectId] || null,

  setPolling: (projectId, isPolling) =>
    set((state) => ({
      pollingProjects: { ...state.pollingProjects, [projectId]: isPolling }
    }))
}));
